import React, { useState } from "react";
import { AddFilterBaseFunc, chartAttrs, defaultStyles, graphColors, IDimFacility, IFilterKeys, IFilters, IPlotData, RemoveFilterBaseFunc } from "models/core";
import { DashboardFilterKeys } from "models/mdt";
import { PlotData } from "plotly.js";
import { Dialog } from "@mui/material";
import { DashChart } from "components/common/DashChart";
import { ChartIcons } from "components/common/ChartIcons";
import { chartDimesionsCalc } from "../core";
import watermark from "../../static/qca-black.png";
import { CreateChart } from "components/common/CreateChart";

interface IProps {
	plotData: {[facDiagYearKey: string]: { "Facility": number, "State": number }};
	mdtFacilityRef: IDimFacility[];
	activeFilters: IFilters<IFilterKeys>;
	addFilterFn: AddFilterBaseFunc<typeof DashboardFilterKeys>;
    removeFilterFn: RemoveFilterBaseFunc<typeof DashboardFilterKeys>;
}

/**
 * Reviews by service chart / My MDTs
 */
export function ThreeYearSurvivalChart(props: IProps) {
	const { plotData, mdtFacilityRef, activeFilters } = props;
	const [ chartType, setChartType ] = useState("bar");
    const [ chartOrientation, setChartOrientation ] = useState("v");
	const [ fullScreenOpen, setFullScreenState ] = useState(false);
    
	const chartDimensions = chartDimesionsCalc(50, 12, 4);
	const chartTitle = "Three Year Survival";
	const xtitle = (chartOrientation == "h" ? "" : "Diagnosis Years");
	const ytitle = (chartOrientation == "h" ? "Diagnosis Years" : "");

	//Filter the survival data with the current facility+Cancer filters
	const facName = activeFilters?.Facility?.value[0];

	let cancerName: string = activeFilters?.PrimarySite?.parts[0];
	if (activeFilters?.PrimarySite?.parts?.length == 2) {
		cancerName += activeFilters?.PrimarySite?.parts[1];
	}

    const labels = [/*"Pre-2011", "2011-2013", "2014-2016",*/ "2017-2019"];

	let mdtSurvival: IPlotData = {labels: [], values: []};
    let stateSurvival: IPlotData = {labels: [], values: []};
	if (activeFilters?.Facility?.value.length == 1 && activeFilters?.PrimarySite?.value.length == 1) { 
		let facId = mdtFacilityRef.find(x => x.name == facName)?.facilityId;
		labels.forEach(a => {
			if (plotData[facId+a+cancerName]) {
                mdtSurvival.labels.push(a)
                mdtSurvival.values.push(plotData[facId+a+cancerName].Facility)

                stateSurvival.labels.push(a)
                stateSurvival.values.push(plotData[facId+a+cancerName].State)
			}
		})
	}

	const valuesMDT = mdtSurvival.values;
	const labelsMDT = mdtSurvival.labels;
	const mdt = {"name": "My Facility", "labels": labelsMDT, "values": valuesMDT};

    const valuesState = stateSurvival.values;
	const labelsState = stateSurvival.labels;
	const state = {"name": "Queensland", "labels": labelsState, "values": valuesState};

    const showChart = mdtSurvival.values.findIndex(p => p !== 0) !== -1;

	const tracesArray: any[] = [];
	tracesArray.push(mdt);
    tracesArray.push(state);

	const traces: Partial<PlotData>[] = [];

    //Change y axis range to limit to 100% 
    const style = defaultStyles.defaultBox;
	const bx = chartAttrs(style.height);
    const styling = Object.assign({}, bx.layout)
	if (chartOrientation == "v") {
    	styling.yaxis = { range: [0, 110] };
	}

	if (chartOrientation == "h") {
    	styling.xaxis = { range: [0, 110] };
	}

    tracesArray?.forEach((a, i) => {    
	switch (true) {
				case chartOrientation == "v":
					traces.push({
						x: a.labels,
						y: a.values,
						name: a.name,
						text: a.values.map(p => p.toString() + "%"),
						textposition: "outside",
						textfont: {
							size: 12,
						  },
                        hovertext: a.labels,
						hovertemplate: "%{hovertext}<br>" +
                                    "Survival: %{text}<br>" +
                                    "<extra></extra>",
						type: chartType,
						orientation: chartOrientation,
						marker: {},
						showlegend: false,
					} as unknown as Plotly.PlotData)
				break;

				default:
					traces.push({
						x: a.values,
						y: a.labels,
						name: a.name,
						text: a.values.map(p => p.toString() + "%"),
						textposition: "outside",
						textfont: {
							size: 12,
						  },
                        hovertext: a.labels,
                        hovertemplate: "%{hovertext}<br>" +
                                    "Survival: %{text}<br>" +
                                    "<extra></extra>",
						type: chartType,
						orientation: chartOrientation,
						marker: {},
						showlegend: false,
					} as unknown as Plotly.PlotData)
				break;
		}
	})
	
	const logo = watermark;
	const  fullScreenChart =
		<Dialog
			open={fullScreenOpen || false}
			fullWidth={true}
			maxWidth={false}
			style={{width: (window.innerWidth - 100), 
					height: ((chartDimensions.fullScreenPaperHeight*1.3) + "px"),
					marginLeft: 50,
					marginRight: 50 }}
		>
				<DashChart style={{height: (chartDimensions.fullScreenPaperHeight) + "px"}} 
							heading ={chartTitle} 
							chartKey = {chartTitle+"fullScreen"}
						> 
					<div style={{marginTop: "-70px", float: "right", display: "inline"}}>
					<ChartIcons vbar ={true} hbar ={true}
						table={true}
						scatter={true}
						pie={false}
						downloadAsCsv={true}
						downloadAsPng={true}
						isFullScreen={fullScreenOpen}
						viewAsFullScreen={true}
						tooltipInfo={false}
						chartOrientation={chartOrientation}
						chartType={chartType}
						traces = {traces}
						xLabel = {(chartOrientation=="h" ? ytitle : xtitle)} 
						yLabel = {(chartOrientation=="h" ? xtitle : ytitle)}
						title= {chartTitle}
						setChartOrientation = {setChartOrientation}
						setChartType = {setChartType}
						setFullScreen = {setFullScreenState}
						currentFilters = {activeFilters}
						/>
					<br/>
					{<img
					src={logo}
					alt="Cancer Alliance Queensland"
					width={200}
					style={{marginTop: "20px"}}
						/>}
					</div>    
					{CreateChart(chartDimensions.fullScreenChartHeight * 1.2,
								 chartDimensions.fullScreenChartWidth,
								 chartDimensions.fullScreenPaperHeight,
								 chartOrientation,
								 chartType, 
								 showChart, 
								 xtitle, 
								 ytitle, 
								 mdtSurvival.labels,
								 mdtSurvival.values, 
								 traces,
                                 null,
                                 styling)}
                    {(chartType !== "table" ? <div className="dash-legend" style={{marginTop: "5px"}}>
                    <div>
                        <div className="key" style={{backgroundColor: graphColors.default[0]}}></div> 
                        <div className="title">My Facility</div>
                    </div>
                    <div>
                        <div className="key"style={{backgroundColor: graphColors.default[1]}}></div> 
                        <div className="title">Queensland</div>
                    </div>
                	</div> : null)}
				</DashChart>
		</Dialog>


	return (
		<div className={"paper"} key={chartTitle}
        style={{
            float: "right",
            width: "100%",
			padding: "12px",
        }}
        >  
		<ChartIcons vbar ={true} hbar ={true}
			table={true}
			scatter={true}
			pie={false}
			downloadAsCsv={true}
			downloadAsPng={true}
			isFullScreen={fullScreenOpen}
			viewAsFullScreen={true}
			tooltipInfo={false}
			chartOrientation={chartOrientation}
			chartType={chartType}
			traces = {traces}
			xLabel = {(chartOrientation=="h" ? ytitle : xtitle)} 
			yLabel = {(chartOrientation=="h" ? xtitle : ytitle)}
			title= {chartTitle}
			setChartOrientation = {setChartOrientation}
            setChartType = {setChartType}
			setFullScreen = {setFullScreenState}
			currentFilters = {activeFilters}
			/>
		<DashChart chartKey={chartTitle+"chart"} heading ={chartTitle} style ={{height: chartDimensions.paperHeight + "px", 
																										width: chartDimensions.paperWidth}}>
			{CreateChart(chartDimensions.chartHeight*1.05,
								 chartDimensions.chartWidth,
								 chartDimensions.paperHeight,
								 chartOrientation,
								 chartType, 
								 showChart, 
								 xtitle, 
								 ytitle, 
								 mdtSurvival.labels,
								 mdtSurvival.values, 
								 traces,
                                 null,
                                 styling)}
			{fullScreenChart}
            {(chartType !== "table" ? <div className="dash-legend" style={{marginTop: "5px"}}>
                    <div>
                        <div className="key" style={{backgroundColor: graphColors.default[0]}}></div> 
                        <div className="title">My Facility</div>
                    </div>
                    <div>
                        <div className="key"style={{backgroundColor: graphColors.default[1]}}></div> 
                        <div className="title">Queensland</div>
                    </div>
                	</div> : null)}
            {(!showChart && activeFilters?.Facility?.value.length !== 1 && activeFilters?.PrimarySite?.value.length !== 1 ? 
                <div style={{marginTop: chartDimensions.middleChartText}}>Please select a single Facility and Cancer to generate data</div> : null)}
		</DashChart>
		</div>
	);
}
